import { App as CapacitorApp } from '@capacitor/app'
import type { PluginListenerHandle } from '@capacitor/core'
import { BackgroundTask } from '@capawesome/capacitor-background-task'
import { BackgroundFetch } from '@transistorsoft/capacitor-background-fetch'
import { getIntentWorker } from '@/lib/intent-worker'
import { pendingIntents, inFlightIntents } from '@/lib/intent-queue'
import { getPowerSync } from '@/lib/powersync/client'
import { isNative } from '@/lib/platform'
import { logger } from '@/lib/monitoring'

// iOS gives ~30s in beforeExit; the periodic background-fetch slot is the same
// shape on iOS. Android is more generous (~10min for foreground services,
// ~30s effective for WorkManager fetches), but a single budget keeps the code
// simple and avoids platform-specific edge cases.
const DRAIN_BUDGET_MS = 25_000
const INTENT_POLL_INTERVAL_MS = 500

let started = false
let appStateListener: PluginListenerHandle | null = null

async function waitForPowerSyncIdle(signal: AbortSignal): Promise<void> {
  const ps = getPowerSync()
  if (!ps) return
  try {
    (ps as unknown as { triggerCrudUpload?: () => void }).triggerCrudUpload?.()
  } catch { /* noop — just nudge the uploader */ }
  if (!ps.currentStatus?.dataFlowStatus?.uploading) return
  try {
    await ps.waitForStatus((s) => !s.dataFlowStatus?.uploading, signal)
  } catch { /* aborted or status unsupported — fall through */ }
}

async function waitForIntentsIdle(signal: AbortSignal): Promise<void> {
  while (!signal.aborted) {
    const [pending, inFlight] = await Promise.all([pendingIntents(), inFlightIntents()])
    if (pending.length === 0 && inFlight.length === 0) return
    await new Promise<void>((resolve) => {
      const t = setTimeout(resolve, INTENT_POLL_INTERVAL_MS)
      signal.addEventListener('abort', () => { clearTimeout(t); resolve() }, { once: true })
    })
  }
}

// Shared drain — used by both background-task (drain-on-background) and
// background-fetch (periodic wake). Best-effort: returns when either both
// queues are empty or the budget elapses.
async function drainAll(reason: 'background-task' | 'background-fetch'): Promise<void> {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), DRAIN_BUDGET_MS)
  const startedAt = Date.now()
  try {
    getIntentWorker().scheduleTick()
    await Promise.race([
      Promise.all([
        waitForPowerSyncIdle(ctrl.signal),
        waitForIntentsIdle(ctrl.signal),
      ]),
      new Promise<void>((resolve) => {
        ctrl.signal.addEventListener('abort', () => resolve(), { once: true })
      }),
    ])
  } finally {
    clearTimeout(timer)
    logger.intents.info('background-drain', {
      reason,
      durationMs: Date.now() - startedAt,
      timedOut: ctrl.signal.aborted,
    })
  }
}

export async function registerBackgroundSync(): Promise<void> {
  if (started || !isNative()) return
  started = true

  // Drain-on-background: when the user backgrounds the app, ask iOS/Android for
  // a grace window and flush PowerSync + intents before the WebView suspends.
  appStateListener = await CapacitorApp.addListener('appStateChange', async ({ isActive }) => {
    if (isActive) {
      // Coming back to foreground — kick the worker so any backed-up retries
      // run immediately. visibilitychange already does this on Capacitor's
      // WebView but appStateChange is the canonical signal.
      getIntentWorker().scheduleTick()
      return
    }
    let taskId: string
    try {
      // The cb fires when the OS is about to reclaim our time — finish quickly.
      taskId = await BackgroundTask.beforeExit(() => {
        try { BackgroundTask.finish({ taskId }) } catch { /* already finished */ }
      })
    } catch (err) {
      logger.intents.warn('background-task: beforeExit failed', { error: String(err) })
      return
    }
    try {
      await drainAll('background-task')
    } finally {
      try { BackgroundTask.finish({ taskId }) } catch { /* OS already reclaimed */ }
    }
  })

  // Periodic wake: every ~15min (iOS-throttled, Android via WorkManager) so a
  // queue still holding work hours later eventually drains without user opening
  // the app. iOS terminates kill fetch events — `stopOnTerminate: true` keeps
  // the plugin's behavior predictable on Android too (no headless JS context).
  try {
    await BackgroundFetch.configure(
      {
        minimumFetchInterval: 15,
        stopOnTerminate: true,
        startOnBoot: false,
        enableHeadless: false,
        requiredNetworkType: BackgroundFetch.NETWORK_TYPE_ANY,
      },
      async (taskId) => {
        try { await drainAll('background-fetch') } finally { BackgroundFetch.finish(taskId) }
      },
      async (taskId) => {
        // OS-signalled timeout: finish immediately, don't try to drain more.
        BackgroundFetch.finish(taskId)
      },
    )
  } catch (err) {
    logger.intents.warn('background-fetch: configure failed', { error: String(err) })
  }
}

export async function unregisterBackgroundSync(): Promise<void> {
  if (!started) return
  started = false
  try { await appStateListener?.remove() } catch { /* noop */ }
  appStateListener = null
  try { await BackgroundFetch.stop() } catch { /* noop */ }
}
